import { LinkProps } from "react-router-dom"
import { HomeRegistrationFragment } from "../api/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getSchoolUrl } from "../utils/urls"

type getCertificateDownloadUrlProps = {
  pwContext: ReturnType<typeof usePathwrightContext>
  registration: any
}

//adapted from src/modules/home/learn/utils.js
export const getCertificateDownloadUrl = ({
  pwContext,
  registration
}: getCertificateDownloadUrlProps) => {
  // ensure resource has certificate and path is complete
  if (
    registration.path.progress < 1 ||
    !registration.path.completion_date ||
    !pwContext.school.has_certificate ||
    !registration.resource?.has_certificate
  )
    return null
  // other users shouldn't have access to certificate
  if (!pwContext.me || pwContext.me.id !== registration.user.id) return null

  return getSchoolUrl(
    `/certgen/certificate/${registration.resource.slug}/${
      registration.path.id
    }?sso=${localStorage.getItem("auth_token")}`
  )
}

export const getRegistrationPathLink = (
  registration: HomeRegistrationFragment
) => {
  let link: LinkProps["to"] = {}

  if (registration) {
    const resourceSlug = registration.resource?.slug!
    const cohortId = registration.group?.id!
    const pathId = registration.path?.id

    link = getPathLink({
      resourceSlug: resourceSlug,
      cohortId: cohortId,
      pathId: pathId,
      registrationRole: registration.role
    })
  }

  return link
}

export const getPathLink = ({
  resourceSlug,
  cohortId,
  pathId,
  registrationRole
}: {
  resourceSlug: string
  cohortId: number
  pathId?: number
  registrationRole?: number
}) => {
  let link: LinkProps["to"] = {
    pathname: `/library/${resourceSlug}/${cohortId}/path/`,
    state: {
      setResourceLaunchedFrom: true
    }
  }

  if (registrationRole) {
    // Scope link to user's path when registration is for Moderator-.
    // We may need to optionally do this, or simply define a separate getUserPathLink fn.
    if (registrationRole <= 10) {
      link.search = `?path_id=${pathId}&path_mode=${5}`
    } else if (registrationRole === 15) {
      link.search = `?path_mode=${10}`
    } else if (registrationRole === 20) {
      link.search = `?path_mode=${15}`
    }
  }

  return link
}

//! This function is not being used in the codebase
//Maps a number between a threshold range to a value between 0 and 1
export const mapToRange = (
  value: number,
  minThreshold: number,
  maxThreshold: number
) => {
  // Ensure value is within the bounds of minThreshold and maxThreshold
  if (value < minThreshold || value > maxThreshold) {
    // throw new Error("Value out of bounds")
    console.debug("Value out of bounds:", value)
  }
  // if

  if (value >= maxThreshold) {
    return 1
  }
  // Normalize the value to a range between 0 and 1
  return (value - minThreshold) / (maxThreshold - minThreshold)
}

// Filter out any Source Cohort registrations for which there is only 1 active Cohort
// which the user is registered for (better UX).
export function filterOutSourceRegistration(
  registrations: HomeRegistrationFragment[]
) {
  return registrations.filter(
    (registration) =>
      // Include non-Source Cohorts.
      !registration.group?.is_source_cohort ||
      // Include Source Cohorts which don't have exactly 1 active Cohort.
      registration.resource?.groups?.total !== 1 ||
      // Include Source Cohorts for which the user doesn't have a Cohort registration.
      registrations.every(
        (r) => r.group?.source_cohort_id !== registration.group?.id
      )
  )
}

export function filterRootRegistration(registration: HomeRegistrationFragment) {
  return !registration.group?.parent_section_id
}

export function filterNestedRegistration(
  rootRegistration: HomeRegistrationFragment,
  registration: HomeRegistrationFragment
) {
  return rootRegistration.group?.id === registration.group?.parent_section_id
}
